import { useEffect, useRef, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import type { ImageAttachment } from "../types/chat";

interface AttachmentPreviewsProps {
  attachments: ImageAttachment[];
  onRemove: (id: string) => void;
  style?: CSSProperties;
}

const styles: Record<string, CSSProperties> = {
  strip: {
    display: "flex",
    gap: tokens.space[2],
    overflowX: "auto",
    overflowY: "hidden",
    overscrollBehaviorX: "contain",
    touchAction: "pan-x",
    WebkitOverflowScrolling: "touch",
    paddingTop: 2,
    // Gap between thumbnails and the scroll indicator.
    paddingBottom: 10,
  },
  item: {
    position: "relative",
    flexShrink: 0,
    width: 64,
    height: 64,
  },
  thumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    borderRadius: tokens.radius.md,
    border: `1px solid ${tokens.border.default}`,
    boxSizing: "border-box",
    background: tokens.surface.chip,
  },
  remove: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "none",
    padding: 0,
    background: "rgba(255, 255, 255, 0.92)",
    color: tokens.text.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  },
};

export default function AttachmentPreviews({
  attachments,
  onRemove,
  style,
}: AttachmentPreviewsProps) {
  const { t } = useTranslation();
  const stripRef = useRef<HTMLDivElement>(null);
  const hasAttachments = attachments.length > 0;

  // Mouse wheels only scroll vertically — map that onto the horizontal strip.
  // Native listener: React's onWheel is passive and can't preventDefault.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const atStart = el.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = el.scrollLeft >= max - 1 && event.deltaY > 0;
      if (atStart || atEnd) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [hasAttachments]);

  // Newly added images land off-screen on the right — bring them into view.
  const countRef = useRef(attachments.length);
  useEffect(() => {
    const el = stripRef.current;
    if (el && attachments.length > countRef.current) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }
    countRef.current = attachments.length;
  }, [attachments.length]);

  if (!hasAttachments) return null;

  return (
    <div
      ref={stripRef}
      style={{ ...styles.strip, ...style }}
      aria-label={t("input.attachments")}
      data-attachment-strip=""
    >
      {attachments.map((attachment, index) => (
        <div key={attachment.id} style={styles.item}>
          <img
            src={attachment.previewUrl}
            alt={attachment.file.name}
            style={styles.thumb}
            draggable={false}
          />
          <button
            type="button"
            style={styles.remove}
            onClick={() => onRemove(attachment.id)}
            aria-label={t("input.removeAttachment", { index: index + 1 })}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path
                d="M1.5 1.5l7 7M8.5 1.5l-7 7"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
